import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, ChevronLeft, FolderCheck, Loader2, Plus, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { hasPermission } from "../lib/permissions";
import DeleteWithReasonDialog from "../components/DeleteWithReasonDialog";
import { ITEM_STATE_LABELS, canWrite, itemTone, recordsError } from "../lib/records";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Skeleton } from "../components/ui/skeleton";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const NONE = "__none__";

function ItemBadge({ state }) {
  return <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium whitespace-nowrap ${itemTone(state)}`}>{ITEM_STATE_LABELS[state]}</span>;
}

// Choose among the documents the reader sees (the server checks it again): a small search over the register.
function DocumentChoice({ value, current, onChange }) {
  const [options, setOptions] = useState([]);
  const [search, setSearch] = useState("");
  useEffect(() => {
    const t = setTimeout(() => {
      api.get("/records/documents", { params: { limit: 20, include_closed: true, ...(search ? { search } : {}) } })
        .then(({ data }) => setOptions(data.items)).catch(() => setOptions([]));
    }, 250);
    return () => clearTimeout(t);
  }, [search]);
  const list = current?.visible && !options.some((o) => o.id === current.id) ? [current, ...options] : options;
  return (
    <div className="space-y-2">
      <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher un document…" className="h-10" aria-label="Rechercher un document" />
      <Select value={value || NONE} onValueChange={(v) => onChange(v === NONE ? null : v)}>
        <SelectTrigger className="h-10" data-testid="constitution-document"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value={NONE}>Aucun document (pièce manquante)</SelectItem>
          {list.map((d) => <SelectItem key={d.id} value={d.id}>{d.number} · {d.title}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

function ItemDialog({ open, item, onClose, onDone }) {
  const [form, setForm] = useState({ label: "", order: 0, note: "", document_id: null, active: true });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    if (!open) return;
    setForm(item ? { label: item.label, order: item.order || 0, note: item.note || "", document_id: item.document_id || null, active: item.active }
      : { label: "", order: 0, note: "", document_id: null, active: true });
    setError("");
  }, [open, item]);
  const set = (field) => (value) => setForm((f) => ({ ...f, [field]: value }));
  const hidden = item?.document && !item.document.visible; // linked to a document this reader cannot see: never offer to unlink it blindly
  const submit = async () => {
    setBusy(true);
    setError("");
    const body = { label: form.label.trim(), order: Number(form.order) || 0, note: form.note.trim() };
    if (!hidden) body.document_id = form.document_id;
    try {
      if (item) await api.patch(`/records/constitution/items/${item.id}`, { expected_version: item.version, active: form.active, ...body });
      else await api.post("/records/constitution/items", body);
      toast.success(item ? "Pièce modifiée." : "Pièce ajoutée.");
      onDone();
    } catch (e) {
      setError(recordsError(e, "Enregistrement impossible. Réessayez."));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{item ? "Modifier la pièce" : "Ajouter une pièce"}</DialogTitle>
          <DialogDescription>Une pièce du dossier de constitution de Jamora ; reliez-la au document du registre qui la prouve.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-[1fr_6rem]">
            <div className="space-y-1.5"><Label htmlFor="constitution-label">Pièce</Label>
              <Input id="constitution-label" value={form.label} maxLength={160} onChange={(e) => set("label")(e.target.value)} className="h-10"
                placeholder="Ex. Statuts signés" data-testid="constitution-label" /></div>
            <div className="space-y-1.5"><Label htmlFor="constitution-order">Ordre</Label>
              <Input id="constitution-order" type="number" min={0} max={1000} value={form.order} onChange={(e) => set("order")(e.target.value)} className="h-10" /></div>
          </div>
          <div className="space-y-1.5">
            <Label>Document</Label>
            {hidden ? <p className="text-sm text-slate-500">Reliée à un document que vous ne voyez pas.</p>
              : <DocumentChoice value={form.document_id} current={item?.document} onChange={set("document_id")} />}
          </div>
          <div className="space-y-1.5"><Label htmlFor="constitution-note">Note</Label>
            <Textarea id="constitution-note" value={form.note} rows={2} maxLength={2000} onChange={(e) => set("note")(e.target.value)} /></div>
          {item && (
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <Switch checked={form.active} onCheckedChange={set("active")} />Pièce attendue (décochez pour la retirer de la liste)
            </label>
          )}
        </div>
        {error && <p className="text-sm text-red-600" role="alert">{error}</p>}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Fermer</Button>
          <Button onClick={submit} disabled={busy || form.label.trim().length < 2} data-testid="constitution-submit">
            {busy && <Loader2 size={14} className="mr-1.5 animate-spin" />}{item ? "Enregistrer" : "Ajouter"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function RecordConstitution() {
  const { user } = useAuth();
  const canManage = canWrite(user, "records:manage_templates");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [showRemoved, setShowRemoved] = useState(false);
  const [dialog, setDialog] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const load = useCallback(async () => {
    setError("");
    try {
      const { data: body } = await api.get("/records/constitution/items", { params: { include_inactive: showRemoved } });
      setData(body);
    } catch (e) {
      setError(recordsError(e, "Impossible de charger le dossier de constitution."));
    }
  }, [showRemoved]);
  useEffect(() => { load(); }, [load]);
  const total = data ? Object.values(data.counts).reduce((a, b) => a + b, 0) : 0;

  return (
    <div className="space-y-4" data-testid="constitution-page">
      <Link to="/documents" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"><ChevronLeft size={14} />Documents et contrats</Link>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="font-display font-bold text-2xl text-slate-900">Dossier de constitution</h1>
          <p className="text-sm text-slate-500">Les pièces qui constituent Jamora (statuts, immatriculations, attestations…) et le document du registre qui prouve chacune.</p>
        </div>
        {canManage && <Button onClick={() => setDialog({ item: null })} data-testid="constitution-new"><Plus size={16} className="mr-1.5" />Ajouter une pièce</Button>}
      </div>

      {error && (
        <Card className="p-6 text-center space-y-3">
          <AlertTriangle className="mx-auto text-amber-500" size={20} />
          <p className="text-sm text-slate-700">{error}</p>
          <Button variant="outline" onClick={load}><RefreshCw size={14} className="mr-1.5" />Réessayer</Button>
        </Card>
      )}
      {!data && !error && <div className="space-y-2"><Skeleton className="h-12 w-full" /><Skeleton className="h-12 w-full" /></div>}
      {data && (
        <>
          {(total > 0 || canManage) && (
            <div className="flex flex-wrap items-center justify-between gap-3">
              {total > 0 ? (
                <div className="flex flex-wrap gap-3 text-sm text-slate-600" data-testid="constitution-counts">
                  <span><strong className="text-slate-900">{data.counts.obtained}</strong> obtenue{data.counts.obtained > 1 ? "s" : ""}</span>
                  <span><strong className="text-slate-900">{data.counts.in_progress}</strong> en cours</span>
                  <span><strong className="text-slate-900">{data.counts.missing}</strong> manquante{data.counts.missing > 1 ? "s" : ""}</span>
                </div>
              ) : <span />}
              {canManage && (
                <label className="flex items-center gap-2 text-sm text-slate-600">
                  <Switch checked={showRemoved} onCheckedChange={setShowRemoved} />Afficher les pièces retirées
                </label>
              )}
            </div>
          )}
          {data.items.length === 0 ? (
            <Card className="p-8 text-center space-y-2" data-testid="constitution-empty">
              <FolderCheck className="mx-auto text-slate-300" size={26} />
              <h3 className="font-medium text-slate-900">Aucune pièce attendue pour le moment</h3>
              <p className="text-sm text-slate-500 max-w-md mx-auto">
                {canManage ? "Listez les pièces qui constituent Jamora ; chacune passera à « Obtenue » dès que son document est signé ou enregistré." : "Un responsable du registre n'a pas encore listé les pièces."}
              </p>
            </Card>
          ) : (
            <Card className="px-4 divide-y divide-slate-100" data-testid="constitution-list">
              {data.items.map((i) => (
                <div key={i.id} className={`flex flex-wrap items-center gap-3 py-3 ${i.active ? "" : "opacity-60"}`} data-testid={`constitution-item-${i.id}`}>
                  <div className="min-w-0 flex-1 space-y-0.5">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-medium text-slate-900 break-words">{i.label}</span>
                      <ItemBadge state={i.state} />
                      {!i.active && <span className="text-xs text-slate-500">Retirée</span>}
                    </div>
                    <div className="text-xs text-slate-500">
                      {i.document?.visible && <Link to={`/documents/${i.document.id}`} className="text-jamora hover:underline">{i.document.number} · {i.document.title}</Link>}
                      {i.document && !i.document.visible && "Document que vous ne voyez pas"}
                      {!i.document && "Aucun document relié"}
                      {i.note && <span> · {i.note}</span>}
                    </div>
                  </div>
                  {canManage && <Button variant="outline" size="sm" onClick={() => setDialog({ item: i })} data-testid={`constitution-edit-${i.id}`}>Modifier</Button>}
                  {canManage && hasPermission(user, "records:delete") && <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700" onClick={() => setDeleting(i)} data-testid={`constitution-delete-${i.id}`}>Supprimer…</Button>}
                </div>
              ))}
            </Card>
          )}
        </>
      )}
      <DeleteWithReasonDialog
        target={deleting} testId="delete-constitution" onClose={() => setDeleting(null)}
        title={`Supprimer la pièce « ${deleting?.label} » ?`}
        description="Elle va dans la corbeille et se restaure pendant 30 jours. Le document du registre auquel elle est reliée n'est pas touché."
        onConfirm={async (reason) => {
          try {
            await api.delete(`/records/constitution/items/${deleting.id}`, { data: { reason } });
            toast.success("Mis à la corbeille");
            setDeleting(null);
            load();
          } catch (e) {
            toast.error(recordsError(e, "Impossible de supprimer cette pièce."));
            setDeleting(null);
          }
        }}
      />
      <ItemDialog open={!!dialog} item={dialog?.item} onClose={() => setDialog(null)} onDone={() => { setDialog(null); load(); }} />
    </div>
  );
}
